import * as stylex from '@stylexjs/stylex'

import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import Card from '../../components/Card'
import EmptyState from '../../components/EmptyState'
import Icon from '../../components/Icon'
import PageHeader from '../../components/PageHeader'
import { findPerson, formatMoney, groupSpent, type Overview } from '../../lib/ledger'
import { mobileStyles as styles } from '../mobileStyles'

type GroupsMobilePageProps = {
    data: Overview
    onAddExpense: (groupID: string) => void
    onCreateGroup: () => void
    onOpenGroup: (groupID: string) => void
}

const localStyles = stylex.create({
    grid: {
        display: 'grid',
        gap: '12px',
    },
    top: {
        display: 'grid',
        width: '100%',
        gridTemplateColumns: '44px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: '11px',
        textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
        ':active': {
            opacity: 0.72,
        },
    },
    groupIcon: {
        display: 'flex',
        height: '44px',
        width: '44px',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '0.85rem',
        backgroundColor: '#edf6eb',
        color: '#468554',
    },
    copy: {
        minWidth: 0,
    },
    description: {
        display: '-webkit-box',
        overflow: 'hidden',
        marginTop: '3px',
        color: '#748077',
        fontSize: '11px',
        lineHeight: '1rem',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
    },
    arrow: {
        color: '#91a096',
    },
    footer: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: '10px',
        marginTop: '14px',
        paddingTop: '13px',
        borderTopWidth: 1,
        borderTopColor: '#edf0eb',
        '@media (max-width: 360px)': {
            gridTemplateColumns: '1fr',
        },
    },
    memberSummary: {
        display: 'flex',
        minWidth: 0,
        alignItems: 'center',
        gap: '9px',
    },
    avatarStack: {
        display: 'flex',
        flexShrink: 0,
        paddingLeft: '5px',
    },
    stackedAvatar: {
        marginLeft: '-5px',
        borderRadius: '9999px',
        boxShadow: '0 0 0 2px #fff',
    },
    groupMeta: {
        minWidth: 0,
        overflow: 'hidden',
        color: '#748077',
        fontSize: '11px',
        lineHeight: '1rem',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    expenseButton: {
        '@media (max-width: 360px)': {
            width: '100%',
        },
    },
})

export default function GroupsMobilePage({ data, onAddExpense, onCreateGroup, onOpenGroup }: GroupsMobilePageProps) {
    return (
        <div {...stylex.props(styles.page)}>
            <PageHeader
                action={<Button onClick={onCreateGroup} size="sm"><Icon name="plus" />New</Button>}
                eyebrow="Shared spaces"
                subtitle="Keep each plan in its own clear place."
                title="Your groups"
            />

            {data.groups.length === 0 ? (
                <Card>
                    <EmptyState
                        action={<Button onClick={onCreateGroup}>Create a group</Button>}
                        description="Create a shared space before adding expenses."
                        icon={<Icon name="groups" />}
                        title="Start your first group"
                    />
                </Card>
            ) : (
                <div {...stylex.props(localStyles.grid)}>
                    {data.groups.map((group) => {
                        const members = group.memberIds.map((id) => findPerson(data, id)).filter(Boolean)
                        return (
                            <Card as="article" key={group.id}>
                                <button {...stylex.props(localStyles.top)} onClick={() => onOpenGroup(group.id)} type="button">
                                    <span {...stylex.props(localStyles.groupIcon)}><Icon name="groups" /></span>
                                    <span {...stylex.props(localStyles.copy)}>
                                        <span {...stylex.props(styles.cardTitle)}>{group.name}</span>
                                        <span {...stylex.props(localStyles.description)}>{group.description || 'Shared expenses in one place.'}</span>
                                    </span>
                                    <Icon {...stylex.props(localStyles.arrow)} name="arrow-right" />
                                </button>

                                <div {...stylex.props(localStyles.footer)}>
                                    <div {...stylex.props(localStyles.memberSummary)}>
                                        <div {...stylex.props(localStyles.avatarStack)}>
                                            {members.slice(0, 4).map((person) => person && (
                                                <span {...stylex.props(localStyles.stackedAvatar)} key={person.id}>
                                                    <Avatar person={person} size={28} />
                                                </span>
                                            ))}
                                        </div>
                                        <span {...stylex.props(localStyles.groupMeta)}>{group.memberIds.length} people · {formatMoney(groupSpent(data, group.id), data.currentUser.currency)}</span>
                                    </div>
                                    <Button {...stylex.props(localStyles.expenseButton)} onClick={() => onAddExpense(group.id)} size="sm" variant="secondary">
                                        <Icon name="plus" />Expense
                                    </Button>
                                </div>
                            </Card>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
